import { ReactNode, useCallback, useMemo } from 'react';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, SortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { DashboardGrid } from '../AnalyticsStyles';

// Wraps rectSortingStrategy but zeroes the scale, so mixed-span tiles
// (chart spans 2x2, KPI spans 1x1) don't visually stretch when reordered.
const noScaleSortingStrategy: SortingStrategy = (args) => {
  const transform = rectSortingStrategy(args);
  if (!transform) return transform;
  return { ...transform, scaleX: 1, scaleY: 1 };
};

interface ReorderableGridProps<T> {
  items: T[];
  getKey: (item: T) => string;
  onReorder: (orderedIds: string[]) => void;
  renderItem: (item: T) => ReactNode;
}

export const ReorderableGrid = <T,>({ items, getKey, onReorder, renderItem }: ReorderableGridProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const itemIds = useMemo(() => items.map(getKey), [items, getKey]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((item) => getKey(item) === active.id);
      const newIndex = items.findIndex((item) => getKey(item) === over.id);
      const reordered = arrayMove(items, oldIndex, newIndex);
      onReorder(reordered.map(getKey));
    },
    [items, getKey, onReorder],
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={itemIds} strategy={noScaleSortingStrategy}>
        <DashboardGrid>{items.map((item) => renderItem(item))}</DashboardGrid>
      </SortableContext>
    </DndContext>
  );
};
